import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useExperimentApi } from '../../../../store/api/hooks/experimentApiHook'
import Button from '../../../../elements/button'
import Icon from '../../../../components/icons'
import Dropdown from '../../../../elements/Form/dropdown'
import CommonInputText from '../../../../components/commonInputText'
import { useCommonApi } from '../../../../store/api/hooks/commonApiHook'
import { ErrorResponse } from '../../../../store/api/apiTypes'
import { getSelectedOption, trackEvent } from '../../../../_common/functions'
import { BusinessFunctionDataType, CountryDataType } from '../../../../_config/types'
import toast from 'react-hot-toast'
import Loader from '../../../../components/loader'

interface Props {
    literal: string | null
    KPI: any
    isModalOpen: boolean
    onClose: () => void
    onUpdate: () => void
}

const EditKPIUNITModal: React.FC<Props> = ({ literal, KPI, isModalOpen, onClose, onUpdate }) => {
    const { t } = useTranslation()
    const conmmonApi: any = useCommonApi()
    const experimentAPI = useExperimentApi()
    const [units, setUnits] = useState<any[]>([{ label: t('Add your own KPI Unit'), value: 'custom' }])

    const [selectedUnit, setSelectedUnit] = useState<any>(null)
    const [customUnit, setCustomUnit] = useState<string>('')
    const [custom, setCustom] = useState<boolean>(false)
    const [error, setError] = useState<string | undefined>(undefined)
    const [dndKPIUnit, setDndKPIUnit] = useState<boolean>(false)

    const [prevUnit, setPrevUnit] = useState<string>('')

    const modalRef = useRef<HTMLDialogElement>(null)
    const [working, setWorking] = useState<boolean>(false)
    const [kpis, setKpis] = useState<any[]>([])
    const withoutUnitKpis: Set<string> = new Set(['Conversion', 'Incremental Conversion', 'Total Distribution'])

    const getKpiTypes = () => {
        conmmonApi.getKpiTypes(
            { sort_by: '-created_at', page: 1, per_page: 1000 },
            (data: any) => {
                let interests: any[] = []

                if (Array.isArray(data?.data?.result)) {
                    try {
                        interests = structuredClone(data.data.result)
                            ?.map((kpi: any) => {
                                kpi.label = kpi.name
                                kpi.value = kpi.kpi_literal
                                if (withoutUnitKpis.has(kpi.name)) {
                                    kpi.mode = 'single'
                                }
                                return kpi
                            })
                            .sort((a: any, b: any) => a.label.localeCompare(b.label))
                    } catch (e) {
                        console.error('Error processing KPI types:', e)
                        interests = []
                    }
                }
                setKpis(interests)
            },
            (error: ErrorResponse<BusinessFunctionDataType>) => {
                toast.error(error.message)
            },
        )
    }

    const updateUnit = () => {
        if (custom && !customUnit && !dndKPIUnit) {
            setError(t('Please enter a valid unit'))
            return
        } else if (!custom && !selectedUnit && !dndKPIUnit) {
            setError(t('Please select a unit'))
            return
        }

        let value = ''
        if (!dndKPIUnit) {
            value = custom ? customUnit : selectedUnit.value
        }

        trackEvent('experiment_details_kpi_unit_updated', { experiment_literal: literal, kpi_name: KPI?.label, unit: dndKPIUnit ? '' : value })
        setWorking(true)
        experimentAPI.updateUnitAPI(
            {
                literal,
                kpi_unit: dndKPIUnit ? '' : value,
                literral_value: KPI?.label,
                has_value: !dndKPIUnit,
            },
            (resp: any) => {
                setWorking(false)
                onClose()
                setTimeout(onUpdate, 1500)
            },
            (error: ErrorResponse<CountryDataType[]>) => {
                setWorking(false)
                onClose()
                toast.error(error.message)
            },
        )
    }

    const _handleCloseModal = () => {
        onClose()
    }

    useEffect(() => {
        if (literal && isModalOpen && kpis.length > 0) {
            const kpiNames: string[] = kpis.map((kpi: any) => kpi.name)
            if (kpiNames.includes(KPI?.label)) {
                setCustom(false)
                const kpi = kpis.find((kpi: any) => kpi.name === KPI?.label)
                setUnits(kpi.units.map((unit: any) => ({ label: unit, value: unit }))) //.concat({ label: t('Add your own KPI Unit'), value: 'custom' }) -- We are removing the option to add custom unit from dropdown, as it is available as a separate option and having it in dropdown was causing confusion
                setSelectedUnit(
                    getSelectedOption(
                        KPI?.kpi_unit,
                        kpi.units.map((unit: any) => ({ label: unit, value: unit })),
                    ),
                )
            } else {
                setCustom(true)
                setCustomUnit(KPI?.kpi_unit || '')
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [literal, isModalOpen, kpis])

    // Escape key to close modal
    useEffect(() => {
        if (kpis.length === 0) {
            getKpiTypes()
        }
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                _handleCloseModal()
            }

            // Basic focus trap
            if (e.key === 'Tab' && modalRef.current) {
                const focusableEls = modalRef.current.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
                const firstEl = focusableEls[0]
                const lastEl = Array.from(focusableEls).at(-1) as HTMLElement

                if (e.shiftKey) {
                    // Shift + Tab
                    if (document.activeElement === firstEl) {
                        e.preventDefault()
                        lastEl.focus()
                    }
                } else if (document.activeElement === lastEl) {
                    e.preventDefault()
                    firstEl.focus()
                }
            }
        }

        if (isModalOpen) {
            document.addEventListener('keydown', handleKeyDown)
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalOpen])

    useEffect(() => {
        if (KPI) {
            setPrevUnit(KPI.kpi_unit || t('No-Unit'))
        }
    }, [KPI, t])

    return (
        <dialog ref={modalRef} className={`modal ${isModalOpen ? 'modal-open' : ''}`} aria-modal="true" aria-labelledby="kpi-unit-update-modal" data-testid="modal">
            <div className="modal-box bg-[#FFFFFF] rounded-[0.833rem] w-[26.083rem] outline-none py-[1.042rem] px-[1.542rem]">
                <p className="text-[1rem] font-bold ">{t('Update KPI Unit')}</p>
                <p className="text-[0.667rem] text-[#676767] leading-[1.2] mt-1">{t('Select a Unit from dropdown or type your own to customise how this KPI is displayed')}</p>

                <div className="mt-2 mb-4 flex">
                    <div className="h-[1rem] text-[0.542rem] leading-[0.8rem] rounded-[1.25rem] bg-[#EFEFEF] px-[0.5rem] py-[0.125rem] text-[#313144] font-medium">
                        <span>{t('Current Unit :')} </span>
                        <span className="font-bold">{prevUnit}</span>
                    </div>
                </div>

                <div>
                    <div className="flex gap-[0.417rem] items-center mb-2">
                        <Icon name="check" size={0.667} className="text-green-600" /> <p className="text-[0.667rem] font-semibold">{custom ? t('Add your own KPI unit') : t('Select KPI Unit')} </p>
                    </div>
                    <div className="pl-[1.083rem]">
                        {custom ? (
                            <CommonInputText
                                required={true}
                                placeholder=""
                                disabled={dndKPIUnit}
                                value={customUnit}
                                onChange={(e: any) => {
                                    const value = e.target.value
                                    if (value.length > 10) {
                                        e.target.value = value.slice(0, 10)
                                    }
                                    setCustomUnit(e.target.value)
                                    trackEvent('experiment_details_kpi_unit_custom_entered', { experiment_literal: literal, kpi_name: KPI?.label })
                                }}
                                error={error}
                            />
                        ) : (
                            <Dropdown
                                required={true}
                                placeholder=""
                                value={selectedUnit}
                                disabled={dndKPIUnit}
                                onChange={(option) => {
                                    if (option.value == 'custom') {
                                        setCustom(true)
                                    } else {
                                        trackEvent('experiment_details_kpi_unit_type_selected', { experiment_literal: literal, kpi_name: KPI?.label, unit_type: option.value })
                                        setSelectedUnit(option)
                                    }
                                }}
                                options={units}
                                error={error}
                            />
                        )}

                        {withoutUnitKpis.has(KPI?.label) || custom ? (
                            <div className="flex items-center gap-2 mt-4 mb-12">
                                <input
                                    type="checkbox"
                                    checked={dndKPIUnit}
                                    onChange={(e: any) => {
                                        trackEvent('experiment_details_kpi_unit_hide_toggled', { experiment_literal: literal, kpi_name: KPI?.label, hidden: e.target.checked })
                                        setDndKPIUnit(e.target.checked)
                                        setSelectedUnit(null)
                                        setCustom(false)
                                        setCustomUnit('')
                                    }}
                                />
                                <p className="text-[0.667rem]">{t('Do not Display any Unit for this KPI')}</p>
                            </div>
                        ) : (
                            <div className="mt-16"></div>
                        )}
                    </div>
                </div>

                <div className="flex items-center justify-center gap-4">
                    <Button size="sm" theme="tertiary" className="border" disabled={working} onClick={_handleCloseModal}>
                        <Icon name="close" size={'1rem'} />
                        {t('No, Cancel')}
                    </Button>
                    <Button size="sm" theme="secondary" onClick={updateUnit} disabled={working}>
                        {working ? (
                            <Loader />
                        ) : (
                            <>
                                <Icon name="check" size={1} /> {t('Update Unit')}
                            </>
                        )}
                    </Button>
                </div>
            </div>

            <div className="modal-backdrop fixed inset-0 bg-black/30" aria-hidden="true" onClick={_handleCloseModal}></div>
        </dialog>
    )
}

export default EditKPIUNITModal
